import type { ClientMessage, ServerMessage } from '@cm/protocol';
import { websocketUrl } from './api';
import { useStore } from './store';

/** `isReplay` avisa o pane que o conteudo e scrollback e ele deve limpar antes. */
export type DataHandler = (data: string, isReplay: boolean) => void;

/** Mesma saída, com a sessão, para quem observa todos os panes. */
export type ActivityHandler = (sessionId: string, data: string, isReplay: boolean) => void;

/**
 * Conexao unica com o daemon, compartilhada por todos os panes.
 *
 * Um WebSocket por pane escalaria mal e duplicaria o estado de sessoes; aqui
 * o socket e um so e o roteamento por sessionId acontece do lado do cliente.
 */
class Connection {
  private socket: WebSocket | null = null;
  private handlers = new Map<string, Set<DataHandler>>();
  private activityHooks = new Set<ActivityHandler>();
  private inputHooks = new Set<(sessionId: string, data: string) => void>();
  private resizeHooks = new Set<(sessionId: string) => void>();
  private endHooks = new Set<(sessionId: string) => void>();
  private reconnectTimer: number | null = null;
  private reconnectDelay = 1_000;
  private pingTimer: number | null = null;
  private token: string | null = null;
  private intentionalClose = false;

  connect(token: string): void {
    this.token = token;
    this.intentionalClose = false;
    this.open();
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.clearTimers();
    this.socket?.close();
    this.socket = null;
    // Os handlers NAO sao limpos aqui: cada pane remove o seu no unmount. Se
    // limpassemos, um disconnect/connect transitorio deixaria todos os panes
    // abertos porem sem attach, com terminais eternamente vazios.
  }

  private open(): void {
    if (!this.token) return;
    if (this.socket && (this.socket.readyState === 0 || this.socket.readyState === 1)) return;

    const socket = new WebSocket(websocketUrl(this.token));
    this.socket = socket;

    socket.onopen = () => {
      this.reconnectDelay = 1_000;
      useStore.getState().setConnected(true);
      // Reattacha tudo que ja tinha pane aberto -- o replay repinta a tela.
      for (const sessionId of this.handlers.keys()) {
        this.send({ type: 'attach', sessionId });
      }
      this.pingTimer = window.setInterval(() => this.send({ type: 'ping', at: Date.now() }), 25_000);
    };

    socket.onmessage = (event) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }
      this.dispatch(msg);
    };

    socket.onclose = (event) => {
      this.clearTimers();
      useStore.getState().setConnected(false);
      this.socket = null;
      if (this.intentionalClose) return;
      // 4401 = token invalido; reconectar nao resolve, tem que relogar.
      if (event.code === 4401) {
        useStore.getState().logout('sua sessao expirou; entre de novo');
        return;
      }
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      useStore.getState().setConnected(false);
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(delay * 2, 15_000);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private clearTimers(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private dispatch(msg: ServerMessage): void {
    const store = useStore.getState();
    switch (msg.type) {
      case 'hello':
        store.setUser(msg.user);
        store.setSessions(msg.sessions);
        break;
      case 'attached':
        store.upsertSession(msg.session);
        this.emitData(msg.session.id, msg.replay, true);
        break;
      case 'output':
        this.emitData(msg.sessionId, msg.data, false);
        break;
      case 'session':
        store.upsertSession(msg.session);
        break;
      case 'session-closed':
        store.markSessionClosed(msg.sessionId, msg.exitCode);
        for (const hook of this.endHooks) hook(msg.sessionId);
        break;
      case 'sessions':
        store.setSessions(msg.sessions);
        break;
      case 'error':
        store.pushToast({ kind: 'error', text: msg.message });
        break;
      case 'pong':
        break;
    }
  }

  private emitData(sessionId: string, data: string, isReplay: boolean): void {
    for (const hook of this.activityHooks) hook(sessionId, data, isReplay);
    const set = this.handlers.get(sessionId);
    if (!set) return;
    for (const handler of set) handler(data, isReplay);
  }

  private send(msg: ClientMessage): void {
    if (this.socket?.readyState !== 1) return;
    this.socket.send(JSON.stringify(msg));
  }

  /** Registra um pane. Retorna a funcao de limpeza para o unmount. */
  subscribe(sessionId: string, handler: DataHandler): () => void {
    let set = this.handlers.get(sessionId);
    if (!set) {
      set = new Set();
      this.handlers.set(sessionId, set);
    }
    set.add(handler);
    this.send({ type: 'attach', sessionId });

    return () => {
      const current = this.handlers.get(sessionId);
      if (!current) return;
      current.delete(handler);
      if (current.size === 0) {
        this.handlers.delete(sessionId);
        this.send({ type: 'detach', sessionId });
      }
    };
  }

  sendInput(sessionId: string, data: string): void {
    for (const hook of this.inputHooks) hook(sessionId, data);
    this.send({ type: 'input', sessionId, data });
  }

  resize(sessionId: string, cols: number, rows: number): void {
    for (const hook of this.resizeHooks) hook(sessionId);
    this.send({ type: 'resize', sessionId, cols, rows });
  }

  /** Saída do PTY, inclusive de panes que não estão na tela. */
  onActivity(handler: ActivityHandler): () => void {
    this.activityHooks.add(handler);
    return () => this.activityHooks.delete(handler);
  }

  onInput(handler: (sessionId: string, data: string) => void): () => void {
    this.inputHooks.add(handler);
    return () => this.inputHooks.delete(handler);
  }

  onResize(handler: (sessionId: string) => void): () => void {
    this.resizeHooks.add(handler);
    return () => this.resizeHooks.delete(handler);
  }

  onSessionEnd(handler: (sessionId: string) => void): () => void {
    this.endHooks.add(handler);
    return () => this.endHooks.delete(handler);
  }
}

export const connection = new Connection();
